/**
 * Layout Engine
 *
 * Automatic graph layout for the workflow designer canvas.
 *
 * ## Architecture Decision Record (ADR)
 *
 * ### Decision: Replace D3-force with d3-dag Sugiyama layout
 *
 * **Date**: 2026-04-21
 *
 * **Status**: Accepted
 *
 * **Context**: The original layout engine used D3-force (a physics-based force
 * simulation) to position workflow nodes. While D3-force handles cyclic graphs
 * and is very small (~8KB gzip), it produces poor results for structured
 * workflow DAGs:
 * - Condition branches (true/false) collapse into flat horizontal lines
 * - No deterministic hierarchical positioning
 * - "Format Workflow" often made layouts worse than initial positions
 * - Force simulations are non-deterministic (different results each run)
 *
 * **Decision**: Use d3-dag's Sugiyama algorithm (a layered/hierarchical layout
 * purpose-built for DAGs). This provides:
 * - Proper branch separation (true/false paths visually distinct)
 * - Deterministic output (same input → same layout every time)
 * - Minimized edge crossings (built into the algorithm)
 * - Clean layer-based horizontal/vertical flow
 *
 * **Trade-offs**:
 * - Bundle size: d3-dag (~12-21KB gzip) vs d3-force (~8KB gzip) = +4-13KB
 * - Cyclic graphs: d3-dag requires DAG input, so we pre-process cycles by
 *   temporarily removing back-edges, laying out the acyclic graph, then
 *   restoring them (standard approach used by ELK and dagre)
 * - d3-dag produces normalized coordinates (0-based), which we scale to
 *   pixel positions using configurable spacing
 *
 * **Alternatives considered**:
 * - dagre (~18-30KB gzip): Good Sugiyama but effectively abandoned, no ESM
 * - ELK.js (~140-400KB gzip): Excellent quality but 10-50x the bundle size
 * - Keep D3-force + tune: Physics simulations fundamentally can't produce
 *   reliable hierarchical layouts regardless of parameter tuning
 *
 * **What changed**: Only `calculateLayout()` internals. The `LayoutInput`,
 * `LayoutOutput`, `convertToLayoutInput`, `applyLayoutToNodes`, and
 * `calculateScopeContainerBounds` interfaces remain identical, so nothing
 * upstream (WorkflowDesigner, graph conversion, position persistence) needs
 * modification.
 */

import { graphStratify, sugiyama } from 'd3-dag';
import type { GraphNode } from 'd3-dag';

export type LayoutDirection = 'DOWN' | 'RIGHT';

/**
 * Maps a workflow's UI-facing `layoutOrientation` ('horizontal' | 'vertical')
 * to the layout engine's internal `LayoutDirection` ('RIGHT' | 'DOWN').
 *
 * Kept here so every call site uses the same mapping and the engine's
 * direction vocabulary stays an implementation detail.
 */
export const orientationToDirection = (
  orientation: 'horizontal' | 'vertical'
): LayoutDirection => (orientation === 'horizontal' ? 'RIGHT' : 'DOWN');

export type LayoutNodeInput = {
  id: string;
  width: number;
  height: number;
  /**
   * ID of the scope container this node belongs to (the parent ForEach or
   * Until action whose loop body visually wraps this node). Used by
   * `calculateLayout` to inflate the cross-axis size of scoped nodes so the
   * scope container halo (header + padding) doesn't overlap sibling scope
   * containers or unscoped nodes.
   *
   * Only set for nodes inside a *visually rendered* scope container — i.e.
   * ForEach/Until loop bodies. Children of If/Switch branches are *not*
   * marked here because no scope container is rendered around them.
   * Undefined for nodes outside any loop scope.
   */
  scopeParentId?: string | undefined;
};

export type LayoutEdgeInput = {
  id: string;
  source: string;
  target: string;
  /** Source handle for branch identification (e.g., 'true', 'false', 'loop', 'done') */
  sourceHandle?: string;
};

export type LayoutInput = {
  nodes: LayoutNodeInput[];
  edges: LayoutEdgeInput[];
};

export type LayoutPosition = {
  x: number;
  y: number;
};

export type LayoutOutput = Map<string, LayoutPosition>;

/** Measured dimensions from XY Flow after a node is rendered */
export type NodeMeasured = {
  width?: number | undefined;
  height?: number | undefined;
};

/** Minimal node shape used by scope container bounds calculation */
export type ScopeContainerNodeInput = {
  id: string;
  type?: string | undefined;
  position: LayoutPosition;
  data?: { childNodeIds?: string[] | undefined } | undefined;
  measured?: NodeMeasured | undefined;
};

/** Calculated bounds for a scope container node */
export type ScopeContainerBounds = {
  position: LayoutPosition;
  width: number;
  height: number;
};

// Single source of truth for node dimensions and gap lives in
// layoutConstants.ts. We re-export so existing consumers of layoutEngine
// don't need to change their import path.
import {
  DEFAULT_NODE_WIDTH,
  DEFAULT_NODE_HEIGHT,
  NODE_SPACING as DEFAULT_NODE_GAP,
} from './layoutConstants';
export { DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT, DEFAULT_NODE_GAP };

// Scope container sizing
const SCOPE_CONTAINER_PADDING = 40;
const SCOPE_CONTAINER_HEADER_OFFSET = 28;

// ─── Tuning knobs for post-Sugiyama passes ────────────────────────────────
// Cross-axis offset applied when lifting a branch chain off the source's
// lane. Inside a ForEach/Until scope, we stay tight (110) to match
// `SCOPED_LANE_OFFSET` in workflowHelpers.ts (manual insertion math).
// At top level we use a larger offset so True/False chains read clearly as
// detours from the centered After spine rather than crowding it.
//
// These are *minimum* lane offsets. The actual lift target also accounts
// for the lifted subtree's cross-axis width via `LANE_BUFFER_PX` so a
// wider subtree (e.g. one containing nested sub-branches) doesn't crowd
// the source — see P2 in `liftSingleBranchSubtrees`.
const SCOPED_LANE_OFFSET_PX = 110;
const TOP_LEVEL_LANE_OFFSET_PX = 180;
// Extra clearance kept between the source's edge and the *near* edge of
// the lifted subtree's bounding box. Without this, a wide subtree (e.g.
// a nested If inside a True branch) can have its closest node sit only
// a hair outside the source's footprint even though the subtree centroid
// is one lane offset away.
const LANE_BUFFER_PX = 40;

// Long straight chains (1 parent → 1 child for many steps in a row) carry
// no decision information after the first node. We compact them along the
// flow axis by reducing the layer gap between consecutive nodes once the
// chain reaches COMPACT_CHAIN_MIN_LENGTH, down to LAYER_GAP_COMPACT_FACTOR
// of the default.
//
// Branch-interior chains (chain head's only parent has fan-out > 1, e.g.
// the first action inside an If's True branch) compact from the very first
// transition — the branch is visually contained, so there's no risk of
// confusing the chain with a sequence at the same hierarchy level as the
// branch parent. Top-level chains keep the first transition at the default
// gap so the chain start visually separates from any preceding non-chain
// node (e.g. trigger → first action stays at default).
const COMPACT_CHAIN_MIN_LENGTH = 4;
const COMPACT_CHAIN_START_INDEX_TOP_LEVEL = 2;
const COMPACT_CHAIN_START_INDEX_BRANCH = 1;
const LAYER_GAP_COMPACT_FACTOR = 0.65;

// P6 — Aspect-ratio-aware compaction. After all other post-passes run we
// check the layout's bounding box. If the layer-axis dimension is much
// larger than the cross-axis dimension (i.e. a tall+narrow workflow
// in DOWN mode, or a wide+thin one in RIGHT mode), apply a uniform
// compression along the long axis to bring the ratio back into a
// "reasonable" range. The compression scales every layer-axis gap by
// `aspectCompressionFactor` while keeping nodes non-overlapping.
//
// TARGET_ASPECT_RATIO is the maximum tolerable long:short bbox ratio.
// Beyond this, we compress. 4.0 is the empirical sweet spot — anything
// taller than 4x its width is a long unbroken scroll, but tighter
// thresholds end up firing on short 5–6 node chains that already look
// fine.
const ASPECT_RATIO_THRESHOLD = 4.0;
// Minimum layer-axis spacing between adjacent nodes after compression.
// Keep this comfortably above 0 so nodes still have visible separation
// even in the most-compressed case.
const MIN_COMPRESSED_LAYER_GAP_PX = 16;
// Don't bother with aspect-ratio compression on layouts that don't have
// enough rows to meaningfully benefit. Tiny 2–7 row layouts that happen
// to trip the ratio threshold (e.g. wide horizontal chains) are short
// enough that compression just makes them look cramped.
const MIN_ROWS_FOR_COMPRESSION = 8;

// When biasing an unbalanced If's merge target off the source's lane
// (E in the layout-engine improvements), shift it this fraction of the
// populated branch's lane offset toward that branch. Subtle enough to
// stay visually close to the source but enough to straighten the merge
// edge instead of letting it kink diagonally over the populated chain.
const EMPTY_BRANCH_MERGE_BIAS = 0.15;

// Layout spacing values derived from node dimensions + gap.
// The main Sugiyama path configures d3-dag with pixel-based `nodeSize`/`gap`,
// so its output coordinates are already in pixels. These values are primarily
// used by the fallback layout path and to keep spacing aligned with manual
// node insertion via `+`.
const LAYOUT_CONFIG = {
  /** Center-to-center distance between layers (sequential steps along flow) */
  layerSpacing: DEFAULT_NODE_HEIGHT + DEFAULT_NODE_GAP,
  /** Center-to-center distance between nodes in the same layer (parallel branches) */
  nodeSpacing: DEFAULT_NODE_WIDTH + DEFAULT_NODE_GAP,
  /** Origin offset so nodes don't start at (0,0) */
  originOffset: 100,
};

/**
 * Map layer/cross-axis coordinates to a pixel position based on layout direction.
 * Horizontal (RIGHT): layer → x, cross → y
 * Vertical (DOWN):    cross → x, layer → y
 */
function toPosition(layerPos: number, crossPos: number, isHorizontal: boolean): LayoutPosition {
  return isHorizontal
    ? { x: Math.round(layerPos), y: Math.round(crossPos) }
    : { x: Math.round(crossPos), y: Math.round(layerPos) };
}

/**
 * Convert a node's stored top-left position to its **center** coordinates on
 * the layer and cross axes. `positions` stores React Flow top-left (see the
 * Step 4 comment in `calculateLayout`), but the post-passes (lift, anchor,
 * compress) need centers to do correct ± half-size bbox math against
 * variably-sized nodes. Use this helper anywhere you need
 * `centerCross ± halfCross` or `centerLayer ± halfLayer`.
 */
function getNodeBounds(
  pos: { x: number; y: number },
  size: readonly [number, number],
  isHorizontal: boolean,
): {
  centerLayer: number;
  centerCross: number;
  halfLayer: number;
  halfCross: number;
} {
  const [w, h] = size;
  const halfLayer = isHorizontal ? w / 2 : h / 2;
  const halfCross = isHorizontal ? h / 2 : w / 2;
  // pos.x/y is the React Flow top-left. Add half to land on the center.
  const centerLayer = (isHorizontal ? pos.x : pos.y) + halfLayer;
  const centerCross = (isHorizontal ? pos.y : pos.x) + halfCross;
  return { centerLayer, centerCross, halfLayer, halfCross };
}

/**
 * Detect back-edges that create cycles and return them separately.
 * Uses DFS to find edges that point from a descendant back to an ancestor.
 * These edges are temporarily removed for Sugiyama layout, then the positions
 * are already determined by their source/target nodes.
 */
function breakCycles(
  nodes: LayoutNodeInput[],
  edges: LayoutEdgeInput[]
): { dagEdges: LayoutEdgeInput[]; backEdges: LayoutEdgeInput[] } {
  const nodeIds = new Set(nodes.map((n) => n.id));
  const adj = new Map<string, string[]>();
  for (const n of nodes) adj.set(n.id, []);
  for (const e of edges) {
    if (nodeIds.has(e.source) && nodeIds.has(e.target)) {
      adj.get(e.source)?.push(e.target);
    }
  }

  const visited = new Set<string>();
  const inStack = new Set<string>();
  const backEdgeSet = new Set<string>(); // "source->target" keys

  // Iterative DFS preserves the recursive version's semantics: an edge is a
  // back-edge iff its target is currently on the DFS stack. Each frame tracks
  // its child-iteration index so we resume the traversal after returning from
  // a child instead of restarting it.
  type Frame = { nodeId: string; iter: number };
  for (const root of nodes) {
    if (visited.has(root.id)) continue;
    const stack: Frame[] = [{ nodeId: root.id, iter: 0 }];
    visited.add(root.id);
    inStack.add(root.id);
    while (stack.length > 0) {
      const frame = stack[stack.length - 1]!;
      const children = adj.get(frame.nodeId) ?? [];
      if (frame.iter < children.length) {
        const target = children[frame.iter]!;
        frame.iter++;
        if (inStack.has(target)) {
          backEdgeSet.add(`${frame.nodeId}->${target}`);
        } else if (!visited.has(target)) {
          visited.add(target);
          inStack.add(target);
          stack.push({ nodeId: target, iter: 0 });
        }
      } else {
        inStack.delete(frame.nodeId);
        stack.pop();
      }
    }
  }

  const dagEdges: LayoutEdgeInput[] = [];
  const backEdges: LayoutEdgeInput[] = [];
  for (const e of edges) {
    if (backEdgeSet.has(`${e.source}->${e.target}`)) {
      backEdges.push(e);
    } else {
      dagEdges.push(e);
    }
  }

  return { dagEdges, backEdges };
}

/**
 * Calculate layout positions using the Sugiyama (layered/hierarchical) algorithm.
 *
 * Pipeline:
 * 1. Break cycles (remove back-edges for Until/ForEach loops)
 * 2. Build a DAG with d3-dag's graphStratify
 * 3. Run Sugiyama layout (layering → crossing minimization → coordinate assignment)
 * 4. Scale normalized coordinates to pixel positions
 * 5. Apply branch-aware spacing adjustments
 *
 * @param input - Nodes and edges to layout
 * @param direction - Layout direction ('DOWN' for vertical, 'RIGHT' for horizontal)
 * @returns Map of node IDs to their calculated positions
 */
export const calculateLayout = async (
  input: LayoutInput,
  direction: LayoutDirection = 'DOWN'
): Promise<LayoutOutput> => {
  if (input.nodes.length === 0) {
    return new Map();
  }

  const nodeIds = new Set(input.nodes.map((n) => n.id));
  const validEdges = input.edges.filter(
    (e) => nodeIds.has(e.source) && nodeIds.has(e.target) && e.source !== e.target
  );

  // Step 1: Break cycles for Sugiyama (requires DAG input)
  const { dagEdges } = breakCycles(input.nodes, validEdges);

  // Step 2: Build parent map for graphStratify.
  // Special handling for 'merge' edges: when a condition/switch node has a
  // merge edge (e.g., Check_Status --[merge]--> Final_Response), the merge
  // target should depend on the BRANCH endpoints, not the branch parent.
  // Otherwise d3-dag treats it as a 3rd parallel child and places it in its
  // own lane instead of after the branches converge.
  const parentMap = new Map<string, string[]>();
  for (const node of input.nodes) {
    parentMap.set(node.id, []);
  }

  // Collect merge edges to rewrite their parents
  const mergeEdges: LayoutEdgeInput[] = [];

  for (const edge of dagEdges) {
    if (edge.sourceHandle === 'merge') {
      mergeEdges.push(edge);
    } else {
      parentMap.get(edge.target)?.push(edge.source);
    }
  }

  // For merge edges, find the branch endpoints (last nodes before merge)
  // by following each branch to its terminal nodes
  if (mergeEdges.length > 0) {
    const outgoingMap = new Map<string, string[]>();
    for (const edge of dagEdges) {
      if (edge.sourceHandle !== 'merge') {
        if (!outgoingMap.has(edge.source)) outgoingMap.set(edge.source, []);
        outgoingMap.get(edge.source)!.push(edge.target);
      }
    }

    for (const mergeEdge of mergeEdges) {
      const branchParent = mergeEdge.source;
      // Find branch start nodes (direct children of branchParent via true/false/etc)
      const branchStarts = dagEdges
        .filter((e) => e.source === branchParent && e.sourceHandle && e.sourceHandle !== 'merge')
        .map((e) => e.target);

      // Walk each branch to find its leaf nodes (nodes with no outgoing non-merge edges
      // that stay within the branch)
      const leafNodes: string[] = [];
      for (const start of branchStarts) {
        const visited = new Set<string>();
        const queue: string[] = [start];
        for (let head = 0; head < queue.length; head++) {
          const id = queue[head]!;
          if (visited.has(id)) continue;
          visited.add(id);
          const children = (outgoingMap.get(id) ?? []).filter(
            (c) => c !== mergeEdge.target
          );
          if (children.length === 0) {
            leafNodes.push(id);
          } else {
            queue.push(...children);
          }
        }
      }

      // Merge target depends on all branch leaf nodes (or on branchParent if no branches found)
      if (leafNodes.length > 0) {
        parentMap.get(mergeEdge.target)?.push(...leafNodes);
      } else {
        parentMap.get(mergeEdge.target)?.push(branchParent);
      }
    }
  }

  // Build stratify input
  const stratifyData = input.nodes.map((n) => ({
    id: n.id,
    parentIds: parentMap.get(n.id) ?? [],
  }));

  const isHorizontal = direction === 'RIGHT';

  try {
    // Step 3: Build DAG and run Sugiyama layout.
    //
    // d3-dag's sugiyama() defaults to `nodeSize([1, 1])` and `gap([1, 1])`,
    // which means adjacent nodes/layers end up `nodeSize + gap = 2` units
    // apart in normalized coordinates. We instead configure it with the real
    // pixel node size and the desired pixel gap, so node.x / node.y come back
    // already in pixels and we don't need to apply a scale multiplier.
    //
    // Layout direction is normalized internally as "flow goes down":
    //   node.x = cross-axis (perpendicular to flow)
    //   node.y = layer position (along the flow)
    // We re-map these to screen x/y in toPosition() based on direction.
    const strat = graphStratify();
    const graph = strat(stratifyData);

    // Build a per-node size map so the Sugiyama algorithm uses each node's
    // actual measured dimensions rather than a global fixed size. Sanitize
    // each entry — non-finite or non-positive sizes are treated as missing
    // and fall back to defaults, otherwise Sugiyama would collapse spacing
    // and re-introduce #959-style overlaps.
    //
    // We also build an "inflated" size map in the same pass: nodes that
    // belong to a scope container (ForEach / Until loop body) reserve extra
    // space around themselves equal to the scope container's visual halo so
    // adjacent sibling scopes — or unscoped neighbours — don't get
    // overdrawn by the halo.
    //
    // The halo is asymmetric in *screen* coordinates and is the same
    // regardless of layout direction (per `calculateScopeContainerBounds`,
    // which always subtracts HEADER_OFFSET from minY):
    //
    //   screen X side: SCOPE_CONTAINER_PADDING (both edges)
    //   screen Y side: SCOPE_CONTAINER_PADDING (both edges) +
    //                  SCOPE_CONTAINER_HEADER_OFFSET (top edge, where the
    //                  scope container header chip is rendered)
    //
    // We therefore compute the *screen-axis* inflations once and map them
    // to d3-dag's (cross, layer) tuple based on direction:
    //
    //   DOWN  (vertical):   cross = screen X = width,  layer = screen Y = height
    //   RIGHT (horizontal): cross = screen Y = height, layer = screen X = width
    //
    // Position extraction below still uses the *real* size map, so the
    // actual node draws in the centre of the inflated bounding box and the
    // scope container — sized from real child positions — sits inside the
    // reserved space.
    //
    // Combined into one iteration over input.nodes (hot path: runs on every
    // Format Workflow click and on every initial canvas layout).
    const sanitizeSize = (raw: number | undefined, fallback: number): number => {
      return Number.isFinite(raw) && (raw as number) > 0 ? (raw as number) : fallback;
    };
    const SCOPE_X_INFLATION = 2 * SCOPE_CONTAINER_PADDING;
    const SCOPE_Y_INFLATION = 2 * (SCOPE_CONTAINER_PADDING + SCOPE_CONTAINER_HEADER_OFFSET);
    const scopeCrossInflation = isHorizontal ? SCOPE_Y_INFLATION : SCOPE_X_INFLATION;
    const scopeLayerInflation = isHorizontal ? SCOPE_X_INFLATION : SCOPE_Y_INFLATION;
    const nodeSizeMap = new Map<string, [number, number]>();
    const inflatedSizeMap = new Map<string, [number, number]>();
    for (const n of input.nodes) {
      const nw = sanitizeSize(n.width, DEFAULT_NODE_WIDTH);
      const nh = sanitizeSize(n.height, DEFAULT_NODE_HEIGHT);
      nodeSizeMap.set(n.id, [nw, nh]);

      const baseCross = isHorizontal ? nh : nw;
      const baseLayer = isHorizontal ? nw : nh;
      const crossSize = n.scopeParentId ? baseCross + scopeCrossInflation : baseCross;
      const layerSize = n.scopeParentId ? baseLayer + scopeLayerInflation : baseLayer;
      inflatedSizeMap.set(n.id, [crossSize, layerSize]);
    }

    const layout = sugiyama()
      .nodeSize((node: GraphNode<{ id: string; parentIds: string[] }, unknown>) => {
        const inflated = inflatedSizeMap.get(node.data.id);
        if (inflated) return inflated;
        // Defensive fallback: Sugiyama may invoke nodeSize for nodes the
        // caller didn't pre-register (e.g. internal dummy nodes used to
        // span long edges). Use defaults in d3-dag axis order.
        return isHorizontal
          ? [DEFAULT_NODE_HEIGHT, DEFAULT_NODE_WIDTH]
          : [DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT];
      })
      .gap([DEFAULT_NODE_GAP, DEFAULT_NODE_GAP]);
    layout(graph);

    // Step 4: Extract positions. Coordinates are now already in pixels and
    // represent node *centers*, so subtract half the per-node size (in the
    // correct axis) to get the top-left corner that React Flow expects.
    //   DOWN:  layerPos (node.y) subtracts screen height (nh),
    //          crossPos (node.x) subtracts screen width (nw)
    //   RIGHT: layerPos (node.y) subtracts screen width (nw),
    //          crossPos (node.x) subtracts screen height (nh)
    const positions: LayoutOutput = new Map();

    for (const node of graph.nodes()) {
      const [nw, nh] = nodeSizeMap.get(node.data.id) ?? [DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT];
      const layerHalf = isHorizontal ? nw / 2 : nh / 2;
      const crossHalf = isHorizontal ? nh / 2 : nw / 2;
      const layerPos = node.y - layerHalf + LAYOUT_CONFIG.originOffset;
      const crossPos = node.x - crossHalf + LAYOUT_CONFIG.originOffset;
      positions.set(node.data.id, toPosition(layerPos, crossPos, isHorizontal));
    }

    // Build a shared edge index once so the post-Sugiyama passes don't each
    // re-walk validEdges to rebuild the same adjacency / counts / branch
    // groupings.
    const edgeIndex = buildEdgeIndex(validEdges);

    // Step 5: Enforce branch ordering convention
    // d3-dag assigns cross-axis positions without knowledge of branch semantics.
    // We post-process to ensure: true/loop branches are above (lower cross-axis)
    // and false/done branches are below (higher cross-axis) relative to their
    // parent node. This matches the UX convention users expect.
    enforceBranchOrdering(positions, isHorizontal, edgeIndex);

    // Step 5b: Lift / lower single-branch subtrees.
    // When an If/condition has only ONE non-empty branch (e.g., true populated,
    // false empty) plus a merge/runAfter target, Sugiyama places the populated
    // branch on the same cross-axis lane as the merge target. Shift its
    // subtree off-axis so it visually sits in the proper branch lane.
    liftSingleBranchSubtrees(positions, isHorizontal, edgeIndex, input.nodes, nodeSizeMap);

    // Step 6: Recenter merge targets onto the True/False midpoint.
    // Sugiyama tends to snap unbalanced merge targets to the longer branch's
    // leaf row, which causes the merge edge to curve back across the longer
    // branch and visually collide with trailing add-action buttons.
    recenterMergeTargets(positions, isHorizontal, edgeIndex);

    // Step 7: Compact long sequential chains along the flow axis.
    // A run of nodes with exactly one parent and one child carries no
    // decision information after the first node, so the visual "spine"
    // can use a tighter layer gap without losing scannability.
    compactSequentialChains(positions, isHorizontal, edgeIndex, input.nodes);

    // Step 8: Anchor scope-bound subtrees near their scope parent.
    // ForEach / Until loop bodies (nodes with `scopeParentId`) can drift
    // far laterally from their scope-parent diamond if the rest of the
    // workflow pulls Sugiyama's coordinate assignment the other way. The
    // halo / container around the loop body still draws correctly, but
    // visually the relationship between the loop and its body becomes
    // ambiguous. This pass pulls each scope group's centroid toward its
    // scope parent's cross-axis position, capped so we never push past
    // the next sibling subtree's bbox.
    anchorScopeBoundSubtrees(positions, isHorizontal, input.nodes, nodeSizeMap);

    // Step 9: Aspect-ratio-aware compaction (P6). When the layout's
    // long-axis dimension dwarfs the short axis by more than
    // ASPECT_RATIO_THRESHOLD, compress every layer-axis gap uniformly so
    // the canvas reads as a workflow rather than a never-ending scroll.
    compressLayoutAspectRatio(positions, isHorizontal, input.nodes, nodeSizeMap);

    return positions;
  } catch (err) {
    // Fallback: if d3-dag fails (disconnected components, etc.),
    // use a simple layer-based layout
    console.warn('[layoutEngine] d3-dag Sugiyama failed, using fallback layout:', err);
    return fallbackLayout(input, direction);
  }
};

/**
 * Shared edge index used by post-Sugiyama passes (enforceBranchOrdering and
 * recenterMergeTargets). Built once per layout pass over `validEdges` to avoid
 * repeating the same O(E) adjacency / counting / classification work.
 */
type EdgeIndex = {
  outgoing: Map<string, string[]>;
  incomingCount: Map<string, number>;
  nonMergeIncomingCount: Map<string, number>;
  /** Edges grouped by source, classified by sourceHandle */
  branchGroups: Map<string, {
    trueTargets: string[];
    falseTargets: string[];
    mergeTargets: string[];
  }>;
  /**
   * Source node IDs whose branchGroups entry came from a `loop` / `done`
   * handle rather than `true` / `false`. Used by liftSingleBranchSubtrees
   * to skip ForEach/Until single-branch cases — loop bodies are positioned
   * via `scopeParentId` inflation, not lane lift.
   */
  loopBranchSources: Set<string>;
};

function buildEdgeIndex(edges: LayoutEdgeInput[]): EdgeIndex {
  const outgoing = new Map<string, string[]>();
  const incomingCount = new Map<string, number>();
  const nonMergeIncomingCount = new Map<string, number>();
  const branchGroups = new Map<string, {
    trueTargets: string[];
    falseTargets: string[];
    mergeTargets: string[];
  }>();
  const loopBranchSources = new Set<string>();

  for (const edge of edges) {
    // Downstream adjacency
    let outList = outgoing.get(edge.source);
    if (!outList) {
      outList = [];
      outgoing.set(edge.source, outList);
    }
    outList.push(edge.target);

    // Incoming counts (total + non-merge)
    incomingCount.set(edge.target, (incomingCount.get(edge.target) ?? 0) + 1);
    if (edge.sourceHandle !== 'merge') {
      nonMergeIncomingCount.set(
        edge.target,
        (nonMergeIncomingCount.get(edge.target) ?? 0) + 1
      );
    }

    // Branch groups by source handle
    if (!edge.sourceHandle) continue;
    const handle = edge.sourceHandle;
    const isTrueLike = handle === 'true' || handle === 'loop';
    const isFalseLike = handle === 'false' || handle === 'done';
    const isMerge = handle === 'merge';
    if (!isTrueLike && !isFalseLike && !isMerge) continue;

    if (handle === 'loop' || handle === 'done') {
      loopBranchSources.add(edge.source);
    }

    let group = branchGroups.get(edge.source);
    if (!group) {
      group = { trueTargets: [], falseTargets: [], mergeTargets: [] };
      branchGroups.set(edge.source, group);
    }
    if (isTrueLike) group.trueTargets.push(edge.target);
    else if (isFalseLike) group.falseTargets.push(edge.target);
    else group.mergeTargets.push(edge.target);
  }

  return { outgoing, incomingCount, nonMergeIncomingCount, branchGroups, loopBranchSources };
}

/**
 * Collect branch-exclusive subtree: walk downstream from startIds but stop at:
 * - Nodes in stopIds (other branch roots)
 * - Merge targets (nodes reached via 'merge' handle from the branch parent)
 * - Nodes with 2+ incoming edges (natural merge/join points)
 *
 * Shared between enforceBranchOrdering and recenterMergeTargets to keep their
 * traversal semantics in lockstep — divergence would silently desync the two
 * post-Sugiyama passes.
 */
function collectBranchSubtree(
  startIds: string[],
  stopIds: Set<string>,
  outgoing: Map<string, string[]>,
  incomingCount: Map<string, number>
): Set<string> {
  const startIdSet = new Set(startIds);
  const subtree = new Set<string>();
  const queue: string[] = [...startIds];
  for (let head = 0; head < queue.length; head++) {
    const id = queue[head]!;
    if (subtree.has(id) || stopIds.has(id)) continue;
    // Stop at merge points (nodes with multiple parents) unless it's a start node
    if (!startIdSet.has(id) && (incomingCount.get(id) ?? 0) >= 2) continue;
    subtree.add(id);
    for (const child of outgoing.get(id) ?? []) {
      if (!subtree.has(child)) queue.push(child);
    }
  }
  return subtree;
}

function enforceBranchOrdering(
  positions: LayoutOutput,
  isHorizontal: boolean,
  edgeIndex: EdgeIndex
): void {
  const { outgoing, incomingCount, branchGroups } = edgeIndex;

  for (const [, pair] of branchGroups) {
    if (pair.trueTargets.length === 0 || pair.falseTargets.length === 0) continue;
    // Stop set: other branch roots + merge targets
    const falseStopSet = new Set([...pair.falseTargets, ...pair.mergeTargets]);
    const trueStopSet = new Set([...pair.trueTargets, ...pair.mergeTargets]);

    const trueSubtree = collectBranchSubtree(pair.trueTargets, falseStopSet, outgoing, incomingCount);
    const falseSubtree = collectBranchSubtree(pair.falseTargets, trueStopSet, outgoing, incomingCount);

    // Get the average cross-axis position for each branch
    const avgCross = (nodeIds: Set<string>): number => {
      let sum = 0, count = 0;
      for (const id of nodeIds) {
        const pos = positions.get(id);
        if (pos) {
          sum += isHorizontal ? pos.y : pos.x;
          count++;
        }
      }
      return count > 0 ? sum / count : 0;
    };

    const trueCross = avgCross(trueSubtree);
    const falseCross = avgCross(falseSubtree);

    // Convention: true branch should have LOWER cross-axis value (above / left)
    if (trueCross <= falseCross) continue;

    // Swap: mirror both subtrees around their midpoint
    const mid = (trueCross + falseCross) / 2;
    const allSwapNodes = new Set([...trueSubtree, ...falseSubtree]);

    for (const id of allSwapNodes) {
      const pos = positions.get(id);
      if (!pos) continue;
      if (isHorizontal) {
        pos.y = Math.round(2 * mid - pos.y);
      } else {
        pos.x = Math.round(2 * mid - pos.x);
      }
    }
  }
}

/**
 * Lift / lower branch subtrees so they don't share the main flow lane.
 *
 * When an If / condition node has branch children plus a merge / runAfter
 * target, Sugiyama tends to lay the longest chain (or the only populated
 * branch) on the same cross-axis lane as the merge target. The result is a
 * visually flattened layout where a branch and the merge target look like
 * siblings on the main flow.
 *
 * For each populated branch, shift its subtree by a fixed lane offset
 * (above for true / loop, below for false / done) relative to the source's
 * cross-axis position. Merge targets themselves aren't part of the subtree
 * (the stop set includes `mergeTargets`), so they stay on the source's lane
 * and `recenterMergeTargets` can align them on the True/False midpoint.
 *
 * Runs in two situations:
 *   - Exactly one branch populated: shift that branch off the source's lane
 *     so it doesn't collide with the merge/runAfter target row.
 *   - Both branches populated + merge target(s): shift each branch onto its
 *     respective lane. Sugiyama otherwise keeps the longer chain on the
 *     source's lane and only deflects the shorter one.
 */
function liftSingleBranchSubtrees(
  positions: LayoutOutput,
  isHorizontal: boolean,
  edgeIndex: EdgeIndex,
  nodes: ReadonlyArray<LayoutNodeInput>,
  nodeSizeMap: ReadonlyMap<string, [number, number]>,
): void {
  // Inside a ForEach/Until scope, manual insertion in workflowHelpers.ts
  // uses SCOPED_LANE_OFFSET (110) to position newly-inserted actions, so
  // we keep that magnitude here to make "Format" + "insert via +" produce
  // visually consistent placement. At top level we use a larger offset so
  // the True/False chains clearly visually separate from the centered
  // After spine. (Originally a single LANE_OFFSET = 110 was used here,
  // which kept branch nodes only ~30px outside the diamond's footprint
  // and made the After spine feel crowded.)
  // When a lifted subtree would land inside a scope container's halo,
  // we push it past the halo edge with this much extra clearance so the
  // halo's drop shadow and label don't visually clip the lifted node.
  const SCOPE_CLEARANCE_GAP = 16;

  // Lookup so we can pick the scope-aware vs top-level lane offset per
  // source node without re-walking input.nodes.
  const scopeParentBySource = new Map<string, string | undefined>();
  for (const node of nodes) {
    scopeParentBySource.set(node.id, node.scopeParentId);
  }

  const { outgoing, incomingCount, branchGroups, loopBranchSources } = edgeIndex;

  // Pre-compute scope halo bounds in layout coordinates. Same numbers used
  // by calculateScopeContainerBounds + the scope inflation block earlier in
  // calculateLayout. We need both layer-axis and cross-axis bounds so we
  // only deflect a lifted subtree when it actually shares layers with a
  // scope (otherwise the cross-axis intersection is irrelevant).
  type Halo = {
    minLayer: number; maxLayer: number;
    minCross: number; maxCross: number;
  };
  const haloBounds: Halo[] = [];
  const scopeGroups = new Map<string, LayoutNodeInput[]>();
  for (const node of nodes) {
    if (!node.scopeParentId) continue;
    let arr = scopeGroups.get(node.scopeParentId);
    if (!arr) {
      arr = [];
      scopeGroups.set(node.scopeParentId, arr);
    }
    arr.push(node);
  }
  for (const [, children] of scopeGroups) {
    let minLayer = Infinity, maxLayer = -Infinity;
    let minCross = Infinity, maxCross = -Infinity;
    for (const child of children) {
      const pos = positions.get(child.id);
      if (!pos) continue;
      const size = nodeSizeMap.get(child.id) ?? [DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT];
      const { centerLayer, centerCross, halfLayer, halfCross } = getNodeBounds(pos, size, isHorizontal);
      minLayer = Math.min(minLayer, centerLayer - halfLayer);
      maxLayer = Math.max(maxLayer, centerLayer + halfLayer);
      minCross = Math.min(minCross, centerCross - halfCross);
      maxCross = Math.max(maxCross, centerCross + halfCross);
    }
    if (!Number.isFinite(minLayer)) continue;
    haloBounds.push({
      minLayer: minLayer - SCOPE_CONTAINER_PADDING,
      maxLayer: maxLayer + SCOPE_CONTAINER_PADDING,
      // Header chip sits on the top (minCross) side only — matches the
      // calculateScopeContainerBounds convention.
      minCross: minCross - SCOPE_CONTAINER_PADDING - SCOPE_CONTAINER_HEADER_OFFSET,
      maxCross: maxCross + SCOPE_CONTAINER_PADDING,
    });
  }

  // Returns the subtree's layer-axis bbox span — needed to detect
  // intersection with a halo on the flow axis.
  const subtreeLayerRange = (subtree: Set<string>): {
    minLayer: number; maxLayer: number;
  } | null => {
    let minLayer = Infinity, maxLayer = -Infinity;
    let count = 0;
    for (const id of subtree) {
      const pos = positions.get(id);
      if (!pos) continue;
      const size = nodeSizeMap.get(id) ?? [DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT];
      const { centerLayer, halfLayer } = getNodeBounds(pos, size, isHorizontal);
      minLayer = Math.min(minLayer, centerLayer - halfLayer);
      maxLayer = Math.max(maxLayer, centerLayer + halfLayer);
      count++;
    }
    if (count === 0) return null;
    return { minLayer, maxLayer };
  };

  for (const [source, pair] of branchGroups) {
    // Skip ForEach/Until — their child layout is controlled by
    // `scopeParentId`-based inflation in convertToLayoutInput. Lifting them
    // here would collide with the scope container halo.
    if (loopBranchSources.has(source)) continue;

    const hasTrue = pair.trueTargets.length > 0;
    const hasFalse = pair.falseTargets.length > 0;
    if (!hasTrue && !hasFalse) continue;

    // Lift exists primarily to break the collision between a branch chain
    // and the merge / runAfter target row. When there's no merge target,
    // skip ONLY if both branches are populated — Sugiyama already fans
    // those out into separate cross-axis lanes on its own. The single-
    // populated-side case still needs a lift, otherwise the lone branch
    // sits on the source's main lane and reads as a sequential chain
    // rather than a parallel detour.
    if (pair.mergeTargets.length === 0 && hasTrue && hasFalse) continue;

    const sourcePos = positions.get(source);
    if (!sourcePos) continue;
    const sourceSize = nodeSizeMap.get(source) ?? [DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT];
    const { centerCross: sourceCross, halfCross: sourceHalfCross } = getNodeBounds(
      sourcePos,
      sourceSize,
      isHorizontal,
    );

    // Top-level Ifs get a wider lane offset than scoped ones so True/False
    // chains clearly sit outside the diamond's footprint.
    const LANE_OFFSET = scopeParentBySource.get(source)
      ? SCOPED_LANE_OFFSET_PX
      : TOP_LEVEL_LANE_OFFSET_PX;

    // Shift each populated branch toward its convention lane. We do both
    // sides in one pass so the case of both-populated also pulls the longer
    // chain off the source's lane (Sugiyama otherwise keeps it on-axis).
    const sides: Array<{ targets: string[]; otherTargets: string[]; direction: -1 | 1 }> = [];
    if (hasTrue) {
      sides.push({ targets: pair.trueTargets, otherTargets: pair.falseTargets, direction: -1 });
    }
    if (hasFalse) {
      sides.push({ targets: pair.falseTargets, otherTargets: pair.trueTargets, direction: 1 });
    }

    for (const { targets, otherTargets, direction } of sides) {
      // Don't walk into the merge target's downstream OR into the opposite
      // branch — both should stay where Sugiyama placed them (or where the
      // sibling side puts them on its own iteration).
      const stopSet = new Set([...pair.mergeTargets, ...otherTargets]);
      const subtree = collectBranchSubtree(
        targets,
        stopSet,
        outgoing,
        incomingCount,
      );
      if (subtree.size === 0) continue;

      // Anchor on the branch root (first direct target). Avg-based shift
      // misaligns the actual root when Sugiyama spread the chain across
      // cross-axis lanes — the branch root is the node we want to land at
      // sourceCross + direction*LANE_OFFSET (subject to halo clearance);
      // the rest of the subtree follows by the same delta.
      const branchRootId = targets[0];
      if (!branchRootId) continue;
      const branchRootPos = positions.get(branchRootId);
      if (!branchRootPos) continue;
      const branchRootSize = nodeSizeMap.get(branchRootId) ?? [DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT];
      const { centerCross: branchRootCross, halfCross: branchRootHalfCross } = getNodeBounds(
        branchRootPos,
        branchRootSize,
        isHorizontal,
      );

      // P2 — Lane-aware width: a wider subtree (e.g. one containing a
      // nested If with sub-branches) needs a bigger lane offset so its
      // closest node doesn't crowd the source. We compute the subtree's
      // cross-axis bbox at its current Sugiyama positions and pick
      // whichever requires more clearance: the base LANE_OFFSET or the
      // subtree-half-width + buffer. All coordinates here are CENTERS
      // (via getNodeBounds), so ±halfCross math is well-defined for
      // variably-sized nodes.
      let subtreeMinCross = Infinity;
      let subtreeMaxCross = -Infinity;
      for (const id of subtree) {
        const pos = positions.get(id);
        if (!pos) continue;
        const size = nodeSizeMap.get(id) ?? [DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT];
        const { centerCross, halfCross } = getNodeBounds(pos, size, isHorizontal);
        subtreeMinCross = Math.min(subtreeMinCross, centerCross - halfCross);
        subtreeMaxCross = Math.max(subtreeMaxCross, centerCross + halfCross);
      }
      const subtreeWidth = Number.isFinite(subtreeMinCross)
        ? subtreeMaxCross - subtreeMinCross
        : 0;
      // Minimum offset = source half-width + buffer + half of subtree
      // width. This keeps the *near edge* of the subtree's bbox at least
      // LANE_BUFFER_PX away from the source's edge.
      const widthAwareOffset = sourceHalfCross + LANE_BUFFER_PX + subtreeWidth / 2;
      const effectiveLaneOffset = Math.max(LANE_OFFSET, widthAwareOffset);

      let desiredCross = sourceCross + direction * effectiveLaneOffset;

      // Detect scope halo collisions: if the lifted branch root would land
      // inside any scope container's halo (sharing both layer and cross
      // axes), nudge it to the nearer halo edge that still keeps the
      // branch on the correct side of the source.
      const subtreeLayer = subtreeLayerRange(subtree);
      if (subtreeLayer) {
        for (const halo of haloBounds) {
          // Layer-axis intersection check — only deflect if the lifted
          // chain shares layers with the scope (otherwise we'd shift away
          // from a halo that doesn't visually overlap).
          if (subtreeLayer.maxLayer < halo.minLayer || subtreeLayer.minLayer > halo.maxLayer) continue;

          const rootTop = desiredCross - branchRootHalfCross;
          const rootBottom = desiredCross + branchRootHalfCross;
          // Cross-axis intersection on the proposed position.
          if (rootBottom < halo.minCross || rootTop > halo.maxCross) continue;

          // Push past whichever halo edge keeps the branch on its
          // convention side of the source. Prefer the edge that requires
          // the smaller deviation from the natural lift target so we
          // don't overshoot.
          const candidateAbove = halo.minCross - branchRootHalfCross - SCOPE_CLEARANCE_GAP;
          const candidateBelow = halo.maxCross + branchRootHalfCross + SCOPE_CLEARANCE_GAP;
          const onConventionSide = direction < 0
            ? (c: number) => c < sourceCross
            : (c: number) => c > sourceCross;

          const candidates = [candidateAbove, candidateBelow]
            .filter(onConventionSide)
            .sort((a, b) => Math.abs(a - desiredCross) - Math.abs(b - desiredCross));
          if (candidates.length === 0) continue;
          desiredCross = candidates[0]!;
        }
      }

      const delta = desiredCross - branchRootCross;
      if (Math.abs(delta) < 1 && subtree.size === 1) continue;

      // Walk the subtree in BFS order from the branch root, computing each
      // node's NEW cross-axis position. The goal (P1: lane containment) is
      // to keep every chain node in the branch's lane, not just the root.
      // The previous "apply uniform delta to every subtree node" approach
      // failed when Sugiyama pulled chain interior nodes toward the merge
      // target's centerline — the delta was computed from the root alone,
      // so interior nodes ended up in the wrong column after the shift.
      //
      // Rules per node, after BFS:
      //   - Branch root: pinned to `desiredCross`.
      //   - Sequential chain node (single in-subtree parent with single
      //     in-subtree child): pinned to its parent's NEW cross. The
      //     Sugiyama-determined cross-axis spread is intentionally
      //     discarded for chains because Sugiyama's spread is driven by
      //     downstream constraints (merge target attraction) which we want
      //     to override — the chain belongs in the branch lane.
      //   - Multi-child interior node (nested branching inside the
      //     subtree): preserve relative Sugiyama offset from parent so
      //     nested sub-branches still spread apart.
      //   - Multi-parent node (rare diamond inside subtree): use the
      //     average of its parents' NEW cross values.
      //
      // Subtree-local adjacency built once: who's the in-subtree parent /
      // children of each subtree node?
      const subtreeChildren = new Map<string, string[]>();
      const subtreeParents = new Map<string, string[]>();
      for (const id of subtree) {
        const children = (outgoing.get(id) ?? []).filter((c) => subtree.has(c));
        subtreeChildren.set(id, children);
        for (const c of children) {
          let parents = subtreeParents.get(c);
          if (!parents) {
            parents = [];
            subtreeParents.set(c, parents);
          }
          parents.push(id);
        }
      }

      const newCrossOf = new Map<string, number>();
      newCrossOf.set(branchRootId, desiredCross);

      const bfsQueue: string[] = [branchRootId];
      for (let head = 0; head < bfsQueue.length; head++) {
        const parentId = bfsQueue[head]!;
        const parentNewCross = newCrossOf.get(parentId)!;
        const parentPos = positions.get(parentId)!;
        const parentSize = nodeSizeMap.get(parentId) ?? [DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT];
        // Centers throughout so per-node-size differences in `relOffset`
        // (childCenter − parentCenter) reflect true center-to-center spacing
        // rather than top-left-to-top-left spacing (which biases away from
        // wider children).
        const parentSugiyamaCross = getNodeBounds(parentPos, parentSize, isHorizontal).centerCross;
        const parentChildren = subtreeChildren.get(parentId) ?? [];

        for (const childId of parentChildren) {
          if (newCrossOf.has(childId)) continue;
          const childPos = positions.get(childId);
          if (!childPos) continue;
          const childSize = nodeSizeMap.get(childId) ?? [DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT];
          const childSugiyamaCross = getNodeBounds(childPos, childSize, isHorizontal).centerCross;
          const childParents = subtreeParents.get(childId) ?? [];

          let computedCross: number;
          if (childParents.length === 1) {
            // Single in-subtree parent. If the parent only has this one
            // child in the subtree, treat as chain: align with parent's
            // new cross. Otherwise preserve Sugiyama's relative offset
            // (sub-branching inside the lifted subtree).
            const parentChildCount = parentChildren.length;
            if (parentChildCount === 1) {
              computedCross = parentNewCross;
            } else {
              const relOffset = childSugiyamaCross - parentSugiyamaCross;
              computedCross = parentNewCross + relOffset;
            }
          } else {
            // Multi-parent (in-subtree diamond). Average over the parents
            // whose new cross we've already computed; fall back to
            // parent's new cross + relative offset if no other parents
            // have been visited yet (BFS order should usually cover this).
            const knownParentCrosses: number[] = [];
            for (const p of childParents) {
              const pc = newCrossOf.get(p);
              if (pc !== undefined) knownParentCrosses.push(pc);
            }
            if (knownParentCrosses.length > 0) {
              computedCross =
                knownParentCrosses.reduce((sum, c) => sum + c, 0) /
                knownParentCrosses.length;
            } else {
              const relOffset = childSugiyamaCross - parentSugiyamaCross;
              computedCross = parentNewCross + relOffset;
            }
          }

          newCrossOf.set(childId, computedCross);
          bfsQueue.push(childId);
        }
      }

      // Apply the new cross-axis positions. Any subtree node that BFS
      // didn't reach (orphan inside the subtree set — shouldn't happen,
      // but guard anyway) keeps its Sugiyama-assigned cross.
      // `newCross` is a CENTER value; convert back to React Flow top-left
      // by subtracting the node's own half-size on the cross axis.
      for (const id of subtree) {
        const newCross = newCrossOf.get(id);
        if (newCross === undefined) continue;
        const pos = positions.get(id);
        if (!pos) continue;
        const [w, h] = nodeSizeMap.get(id) ?? [DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT];
        const halfCross = isHorizontal ? h / 2 : w / 2;
        if (isHorizontal) {
          pos.y = Math.round(newCross - halfCross);
        } else {
          pos.x = Math.round(newCross - halfCross);
        }
      }
    }
  }
}

/**
 * Recenter merge targets onto the cross-axis midpoint between their parent
 * branches. Sugiyama tends to snap merge targets to the longer branch's
 * leaf row when branches are unbalanced, causing the merge edge to curve
 * back across the longer branch and visually collide with trailing
 * add-action buttons. Pulling the target onto the True/False average keeps
 * the merge edge symmetric.
 */
function recenterMergeTargets(
  positions: LayoutOutput,
  isHorizontal: boolean,
  edgeIndex: EdgeIndex
): void {
  const { outgoing, incomingCount, nonMergeIncomingCount, branchGroups } = edgeIndex;

  for (const [source, group] of branchGroups) {
    if (group.mergeTargets.length === 0) continue;
    if (group.trueTargets.length === 0 && group.falseTargets.length === 0) continue;

    const falseStopSet = new Set([...group.falseTargets, ...group.mergeTargets]);
    const trueStopSet = new Set([...group.trueTargets, ...group.mergeTargets]);

    const trueSubtree = collectBranchSubtree(group.trueTargets, falseStopSet, outgoing, incomingCount);
    const falseSubtree = collectBranchSubtree(group.falseTargets, trueStopSet, outgoing, incomingCount);

    const avgCross = (nodeIds: Set<string>): number | undefined => {
      let sum = 0, count = 0;
      for (const id of nodeIds) {
        const pos = positions.get(id);
        if (pos) {
          sum += isHorizontal ? pos.y : pos.x;
          count++;
        }
      }
      return count > 0 ? sum / count : undefined;
    };

    const trueAvg = avgCross(trueSubtree);
    const falseAvg = avgCross(falseSubtree);
    const sourcePos = positions.get(source);

    // Determine the centerline merge targets should converge on.
    let centerline: number | undefined;
    if (trueAvg !== undefined && falseAvg !== undefined) {
      centerline = Math.round((trueAvg + falseAvg) / 2);
    } else if ((trueAvg !== undefined || falseAvg !== undefined) && sourcePos) {
      // Only one side is populated. Aligning the merge target exactly with
      // the source forces the merge edge straight along the flow axis, but
      // when the populated branch is far off-axis the merge edge then runs
      // diagonally *over* the populated chain to reach the merge target.
      // Bias the centerline slightly toward the populated side (a small
      // fraction of its lane offset) so the merge edge straightens without
      // pulling the target visually inside the branch's lane.
      const sourceCross = isHorizontal ? sourcePos.y : sourcePos.x;
      const populatedAvg = trueAvg ?? falseAvg!;
      centerline = Math.round(
        sourceCross + (populatedAvg - sourceCross) * EMPTY_BRANCH_MERGE_BIAS,
      );
    }
    if (centerline === undefined) continue;

    // Eligible merge targets: skip those with non-merge parents (placement
    // is owned elsewhere) and those reached from multiple If/Switch sources
    // (ambiguous centerline).
    const eligibleTargets = group.mergeTargets.filter((target) => {
      if ((nonMergeIncomingCount.get(target) ?? 0) > 0) return false;
      if ((incomingCount.get(target) ?? 0) >= 2) return false;
      return positions.has(target);
    });

    if (eligibleTargets.length === 0) continue;

    if (eligibleTargets.length === 1) {
      const pos = positions.get(eligibleTargets[0]!)!;
      if (isHorizontal) pos.y = centerline;
      else pos.x = centerline;
      continue;
    }

    // Multiple merge targets behave like parallel siblings — fan them out
    // symmetrically around the centerline rather than stacking them on it.
    // Sort by current cross-axis position so an existing relative ordering
    // (driven by Sugiyama crossing minimization) is preserved.
    const PARALLEL_LANE_SPACING = 110;
    const sorted = [...eligibleTargets].sort((a, b) => {
      const pa = positions.get(a)!;
      const pb = positions.get(b)!;
      return (isHorizontal ? pa.y - pb.y : pa.x - pb.x);
    });
    const n = sorted.length;
    for (let i = 0; i < n; i++) {
      const target = sorted[i]!;
      const pos = positions.get(target)!;
      // Center the set: for n=2 the offsets are [-0.5, +0.5] * spacing;
      // for n=3 they are [-1, 0, +1] * spacing; etc.
      const offset = (i - (n - 1) / 2) * PARALLEL_LANE_SPACING;
      const newCross = Math.round(centerline + offset);
      if (isHorizontal) pos.y = newCross;
      else pos.x = newCross;
    }
  }
}

/**
 * Compact long sequential chains along the flow axis.
 *
 * Sugiyama spaces every adjacent layer pair with the same gap, so a chain
 * of N sequential nodes (1 parent → 1 child → 1 child → …) consumes the
 * same vertical (or horizontal) real estate as N independent branches.
 * For chains of length ≥ COMPACT_CHAIN_MIN_LENGTH, we pull each node past
 * COMPACT_CHAIN_START_INDEX closer to its predecessor by a fixed factor.
 *
 * To avoid creating a visible "gap snap" between the chain's last node and
 * whatever follows it, we apply the final cumulative shift to everything
 * downstream of the chain too. The chain's relationship with its successor
 * stays at the default spacing.
 *
 * Skip conditions (each one disqualifies the *whole* chain):
 *   - Any node in the chain is a scope container child (`scopeParentId`):
 *     scope inflation already governs that node's spacing.
 *   - Any node has ≥2 incoming or ≥2 outgoing edges in the global graph
 *     (the chain isn't really sequential at that step).
 *
 * The compaction is applied in-place, in chain order, so subsequent nodes
 * inherit the cumulative shift from their predecessors.
 */
function compactSequentialChains(
  positions: LayoutOutput,
  isHorizontal: boolean,
  edgeIndex: EdgeIndex,
  nodes: ReadonlyArray<LayoutNodeInput>,
): void {
  const { outgoing, incomingCount } = edgeIndex;

  // A "chain-eligible" node has at most one parent and at most one child
  // in the *non-merge* graph view — i.e. it sits in a linear run of
  // nodes (not a fan-out, not a fan-in). The predicate intentionally uses
  // `<= 1` so graph roots (incomingCount === 0) and chain tails leading
  // to leaves (fan-out === 0) also qualify; what disqualifies a node is
  // having multiple parents or multiple children. merge edges
  // (sourceHandle === 'merge') were already rewritten upstream so they
  // don't double-count incoming edges.
  const fanOutOf = (id: string): number => (outgoing.get(id) ?? []).length;
  const scopedIds = new Set(nodes.filter((n) => n.scopeParentId).map((n) => n.id));
  const isSequential = (id: string): boolean =>
    !scopedIds.has(id) &&
    (incomingCount.get(id) ?? 0) <= 1 &&
    fanOutOf(id) <= 1;

  // Build the parent map once so we can detect branch-interior chain heads
  // (a sequential node whose only parent has fan-out > 1, e.g. the first
  // node inside an If's True branch). edgeIndex.outgoing gives source → []
  // targets; we invert it to target → source.
  const firstParentOf = new Map<string, string>();
  for (const [src, targets] of outgoing) {
    for (const t of targets) {
      if (!firstParentOf.has(t)) firstParentOf.set(t, src);
    }
  }

  // Pre-compute which nodes are "chain bodies" — i.e. already covered by
  // walking some upstream sequential node's chain. A node is a chain body
  // of node P iff P is sequential and X is P's only child. Anything not
  // marked here is a candidate chain head.
  //
  // The previous predicate `!isSequential(id) || incomingCount === 0`
  // incorrectly rejected branch roots: a sequential node whose parent has
  // fan-out > 1 (e.g. Compose_4 inside an If's True branch) was treated as
  // a chain *body*, but no chain walking from the parent ever reached it
  // because the parent's `children.length !== 1` filter excludes
  // multi-child parents. Result: the branch-interior chain was missed
  // entirely and never compacted. This precomputed `isChainBody` set is
  // built strictly from sequential single-child parents, so branch roots
  // are correctly excluded and end up as chain starts on their own.
  const isChainBody = new Set<string>();
  for (const node of nodes) {
    if (scopedIds.has(node.id)) continue;
    if (!isSequential(node.id)) continue;
    const children = outgoing.get(node.id) ?? [];
    if (children.length !== 1) continue;
    const child = children[0]!;
    if (!isSequential(child)) continue;
    isChainBody.add(child);
  }

  const visited = new Set<string>();
  const chainStarts: string[] = [];
  for (const node of nodes) {
    if (scopedIds.has(node.id)) continue;
    if (isChainBody.has(node.id)) continue;
    const children = outgoing.get(node.id) ?? [];
    if (children.length !== 1) continue;
    if (!isSequential(children[0]!)) continue;
    chainStarts.push(node.id);
  }

  const gapDelta = Math.round(DEFAULT_NODE_GAP * (1 - LAYER_GAP_COMPACT_FACTOR));
  if (gapDelta <= 0) return;

  /**
   * Branch-interior chain heads (sequential first node of a chain whose
   * lone parent has fan-out > 1) get the more aggressive start index so
   * the chain reads as a tightly-grouped unit inside the branch. Top-level
   * chains (graph root, or chain head whose parent has fan-out 1 which
   * can't actually happen — that parent would be sequential and would
   * make the head a chain body — or the head is itself non-sequential)
   * keep the default-start behavior.
   */
  const startIndexFor = (chainHeadId: string): number => {
    const parent = firstParentOf.get(chainHeadId);
    if (parent === undefined) return COMPACT_CHAIN_START_INDEX_TOP_LEVEL;
    return (outgoing.get(parent)?.length ?? 0) > 1
      ? COMPACT_CHAIN_START_INDEX_BRANCH
      : COMPACT_CHAIN_START_INDEX_TOP_LEVEL;
  };

  // Helper: walk downstream from a starting node and shift every reachable
  // node by `delta` along the flow axis. Used to keep the chain-to-successor
  // gap at the default after we compact the chain's interior — otherwise the
  // last compacted node would leave a visible jump before the next branch
  // or terminal node.
  //
  // Two safety constraints:
  //
  //   1. **Stop at fan-in nodes** (incomingCount >= 2). If two compacted
  //      chains converge into a shared join node, the join sits at the
  //      Sugiyama-assigned position that accommodates *both* upstream
  //      chains. Shifting it relative to one chain would distort the other
  //      and can invert parent/child ordering along the flow axis.
  //
  //   2. **Track already-shifted nodes across the entire pass.** A node
  //      reachable from multiple chain tails must not be shifted more than
  //      once. We thread a shared `globalShifted` set across all chain
  //      iterations so subsequent chains see prior shifts and skip already-
  //      handled subtrees.
  const globalShifted = new Set<string>();
  const shiftSubtree = (startId: string, delta: number): void => {
    if (globalShifted.has(startId)) return;
    // Don't shift through a join — the shared join node sits where Sugiyama
    // placed it to accommodate every upstream path.
    if ((incomingCount.get(startId) ?? 0) >= 2) return;
    const queue: string[] = [startId];
    globalShifted.add(startId);
    for (let head = 0; head < queue.length; head++) {
      const id = queue[head]!;
      const pos = positions.get(id);
      if (pos) {
        if (isHorizontal) pos.x = Math.round(pos.x - delta);
        else pos.y = Math.round(pos.y - delta);
      }
      for (const child of outgoing.get(id) ?? []) {
        if (globalShifted.has(child)) continue;
        if ((incomingCount.get(child) ?? 0) >= 2) continue;
        globalShifted.add(child);
        queue.push(child);
      }
    }
  };

  for (const start of chainStarts) {
    // Walk forward collecting the sequential chain.
    const chain: string[] = [start];
    let cursor = start;
    while (true) {
      const children = outgoing.get(cursor) ?? [];
      if (children.length !== 1) break;
      const next = children[0]!;
      if (!isSequential(next)) break;
      if (visited.has(next)) break;
      chain.push(next);
      visited.add(next);
      cursor = next;
    }

    if (chain.length < COMPACT_CHAIN_MIN_LENGTH) continue;

    // Shift each node past the start index toward its predecessor by a
    // cumulative delta. The shift is applied along the flow (layer) axis.
    // Branch-interior chains use start_index=1 so the first transition
    // also tightens (the branch is visually contained, so there's no
    // top-level chain head to preserve); top-level chains use
    // start_index=2 so trigger→first-action stays at default spacing.
    const startIndex = startIndexFor(start);
    let cumulative = 0;
    for (let i = startIndex; i < chain.length; i++) {
      cumulative += gapDelta;
      const id = chain[i]!;
      const pos = positions.get(id);
      if (!pos) continue;
      if (isHorizontal) pos.x = Math.round(pos.x - cumulative);
      else pos.y = Math.round(pos.y - cumulative);
    }

    // Carry the final shift through everything downstream of the chain's
    // tail so the gap to the next non-chain node (e.g. a branch root)
    // stays at the default rather than ballooning by `cumulative`. We
    // stop at join nodes and skip any nodes already shifted by a prior
    // chain so a shared subtree isn't double-shifted.
    const tailId = chain[chain.length - 1]!;
    for (const downstreamRoot of outgoing.get(tailId) ?? []) {
      shiftSubtree(downstreamRoot, cumulative);
    }
  }
}

/**
 * P7 — Anchor scope-bound subtrees near their scope parent.
 *
 * A ForEach / Until loop's body (each child node has `scopeParentId`
 * pointing at the loop) is layered by Sugiyama independently of the loop
 * source's cross-axis position. Sugiyama's coordinate-assignment can pull
 * the loop body far laterally from the loop diamond when other branches /
 * chains downstream attract the layout — visually, the loop body looks
 * orphaned, disconnected from the loop that owns it.
 *
 * This pass computes each scope group's centroid (cross-axis midpoint of
 * its children's bounding boxes) and shifts the entire group so the
 * centroid aligns with the scope parent's cross-axis position. We never
 * touch layer-axis positions (so the body's vertical/horizontal flow
 * stays intact) and we cap the shift to avoid overrunning a neighboring
 * scope group's bbox.
 */
function anchorScopeBoundSubtrees(
  positions: LayoutOutput,
  isHorizontal: boolean,
  nodes: ReadonlyArray<LayoutNodeInput>,
  nodeSizeMap: ReadonlyMap<string, [number, number]>,
): void {
  // Group scoped nodes by their `scopeParentId`.
  const scopeGroups = new Map<string, LayoutNodeInput[]>();
  for (const node of nodes) {
    if (!node.scopeParentId) continue;
    let arr = scopeGroups.get(node.scopeParentId);
    if (!arr) {
      arr = [];
      scopeGroups.set(node.scopeParentId, arr);
    }
    arr.push(node);
  }
  if (scopeGroups.size === 0) return;

  // Pre-compute every scope group's bbox (cross + layer axes) so we can
  // (a) align centroid to scope parent and (b) clamp shifts against
  // every other node's bbox at overlapping layers (scoped or unscoped).
  type GroupBox = {
    scopeParentId: string;
    childIds: Set<string>;
    children: LayoutNodeInput[];
    centroidCross: number;
    minCross: number;
    maxCross: number;
    minLayer: number;
    maxLayer: number;
  };
  const groupBoxes: GroupBox[] = [];
  for (const [scopeParentId, children] of scopeGroups) {
    let minCross = Infinity;
    let maxCross = -Infinity;
    let minLayer = Infinity;
    let maxLayer = -Infinity;
    for (const child of children) {
      const pos = positions.get(child.id);
      if (!pos) continue;
      const size = nodeSizeMap.get(child.id) ?? [DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT];
      const { centerCross, centerLayer, halfCross, halfLayer } = getNodeBounds(pos, size, isHorizontal);
      minCross = Math.min(minCross, centerCross - halfCross);
      maxCross = Math.max(maxCross, centerCross + halfCross);
      minLayer = Math.min(minLayer, centerLayer - halfLayer);
      maxLayer = Math.max(maxLayer, centerLayer + halfLayer);
    }
    if (!Number.isFinite(minCross)) continue;
    groupBoxes.push({
      scopeParentId,
      childIds: new Set(children.map((c) => c.id)),
      children,
      centroidCross: (minCross + maxCross) / 2,
      minCross,
      maxCross,
      minLayer,
      maxLayer,
    });
  }
  if (groupBoxes.length === 0) return;

  // Cross-axis halo half-widths a group needs on each side: leading edge
  // gets PADDING + HEADER_OFFSET, trailing edge gets PADDING.
  const haloLeading = SCOPE_CONTAINER_PADDING + SCOPE_CONTAINER_HEADER_OFFSET;
  const haloTrailing = SCOPE_CONTAINER_PADDING;

  // Build a flat list of all node bboxes for the layer-axis overlap check.
  type NodeBox = {
    id: string;
    minCross: number;
    maxCross: number;
    minLayer: number;
    maxLayer: number;
    isScoped: boolean;
  };
  const allBoxes: NodeBox[] = [];
  for (const node of nodes) {
    const pos = positions.get(node.id);
    if (!pos) continue;
    const size = nodeSizeMap.get(node.id) ?? [DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT];
    const { centerCross, centerLayer, halfCross, halfLayer } = getNodeBounds(pos, size, isHorizontal);
    allBoxes.push({
      id: node.id,
      minCross: centerCross - halfCross,
      maxCross: centerCross + halfCross,
      minLayer: centerLayer - halfLayer,
      maxLayer: centerLayer + halfLayer,
      isScoped: !!node.scopeParentId,
    });
  }

  for (const box of groupBoxes) {
    const scopeParentPos = positions.get(box.scopeParentId);
    if (!scopeParentPos) continue;
    const scopeParentSize = nodeSizeMap.get(box.scopeParentId) ?? [DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT];
    // Anchor on the scope parent's CENTER (not its top-left) so a
    // variable-sized parent doesn't bias the shift by half its own size.
    const targetCross = getNodeBounds(scopeParentPos, scopeParentSize, isHorizontal).centerCross;
    const rawDelta = targetCross - box.centroidCross;
    if (Math.abs(rawDelta) < 1) continue;

    // Clamp the shift against every other node (scoped or unscoped) whose
    // layer-axis bbox overlaps this group's. A node's halo "claim" on the
    // cross axis is PADDING + HEADER_OFFSET on the leading side and
    // PADDING on the trailing side (only for scoped nodes; unscoped nodes
    // are kept clear by half-padding so the halo's drop shadow doesn't
    // visually clip them).
    let allowedDelta = rawDelta;
    for (const other of allBoxes) {
      if (box.childIds.has(other.id)) continue;
      if (other.id === box.scopeParentId) continue;
      // Skip if layer-axis bboxes don't overlap.
      if (other.maxLayer < box.minLayer || other.minLayer > box.maxLayer) continue;

      const otherHaloLeading = other.isScoped ? haloLeading : SCOPE_CONTAINER_PADDING / 2;
      const otherHaloTrailing = other.isScoped ? haloTrailing : SCOPE_CONTAINER_PADDING / 2;

      if (rawDelta > 0 && other.minCross >= box.maxCross) {
        // Moving toward higher cross; `other` is on the far side. The
        // separation between our trailing edge + halo and other's
        // leading edge + halo must stay non-negative.
        const required = haloTrailing + otherHaloLeading;
        const maxAllowed = other.minCross - box.maxCross - required;
        if (maxAllowed < allowedDelta) allowedDelta = Math.max(0, maxAllowed);
      } else if (rawDelta < 0 && other.maxCross <= box.minCross) {
        // Moving toward lower cross.
        const required = haloLeading + otherHaloTrailing;
        const minAllowed = other.maxCross - box.minCross + required;
        if (minAllowed > allowedDelta) allowedDelta = Math.min(0, minAllowed);
      }
    }

    if (Math.abs(allowedDelta) < 1) continue;
    for (const child of box.children) {
      const pos = positions.get(child.id);
      if (!pos) continue;
      if (isHorizontal) pos.y = Math.round(pos.y + allowedDelta);
      else pos.x = Math.round(pos.x + allowedDelta);
    }
  }
}

/**
 * P6 — Compress layer-axis spacing when the layout is too long+narrow.
 *
 * After every other post-pass, compute the layout's bounding box. If the
 * long-axis dimension exceeds ASPECT_RATIO_THRESHOLD × the short-axis
 * dimension, scale every layer-axis gap between consecutive rows by
 * `targetRatio / actualRatio`, clamped so the *minimum* gap stays at
 * least MIN_COMPRESSED_LAYER_GAP_PX (no node overlap).
 *
 * Implementation detail: we work at the *row* level rather than the
 * per-node level. A "row" is a unique layer-axis coordinate (the top
 * edge of a layer). Rows are sorted, gaps computed, scaled, and a
 * cumulative offset is applied to every node belonging to each row.
 * This keeps nodes within the same row aligned (no row-internal
 * deformation) while shrinking inter-row gaps uniformly.
 */
function compressLayoutAspectRatio(
  positions: LayoutOutput,
  isHorizontal: boolean,
  nodes: ReadonlyArray<LayoutNodeInput>,
  nodeSizeMap: ReadonlyMap<string, [number, number]>,
): void {
  if (nodes.length === 0) return;

  // Compute current bbox.
  let minLayer = Infinity;
  let maxLayer = -Infinity;
  let minCross = Infinity;
  let maxCross = -Infinity;
  for (const node of nodes) {
    const pos = positions.get(node.id);
    if (!pos) continue;
    const size = nodeSizeMap.get(node.id) ?? [DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT];
    const { centerCross, centerLayer, halfCross, halfLayer } = getNodeBounds(pos, size, isHorizontal);
    minLayer = Math.min(minLayer, centerLayer - halfLayer);
    maxLayer = Math.max(maxLayer, centerLayer + halfLayer);
    minCross = Math.min(minCross, centerCross - halfCross);
    maxCross = Math.max(maxCross, centerCross + halfCross);
  }
  if (!Number.isFinite(minLayer)) return;

  const layerSpan = maxLayer - minLayer;
  const crossSpan = maxCross - minCross;
  if (crossSpan <= 0) return;

  const ratio = layerSpan / crossSpan;
  if (ratio <= ASPECT_RATIO_THRESHOLD) return; // already in range

  // Target ratio = ASPECT_RATIO_THRESHOLD. To achieve that we want the
  // new layer span to be `ASPECT_RATIO_THRESHOLD * crossSpan`. The
  // compression factor for layer-axis gaps is therefore the ratio of
  // target/actual layer span — but applied only to the *gaps*, not to
  // node heights themselves.
  const targetLayerSpan = ASPECT_RATIO_THRESHOLD * crossSpan;
  if (targetLayerSpan >= layerSpan) return;

  // Walk all nodes, bin them by their top edge (layer-axis row).
  type Row = { topLayer: number; nodeIds: string[]; height: number };
  const rowByTop = new Map<number, Row>();
  for (const node of nodes) {
    const pos = positions.get(node.id);
    if (!pos) continue;
    const [w, h] = nodeSizeMap.get(node.id) ?? [DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT];
    const layerSize = isHorizontal ? w : h;
    const topLayer = isHorizontal ? pos.x : pos.y;
    let row = rowByTop.get(topLayer);
    if (!row) {
      row = { topLayer, nodeIds: [], height: 0 };
      rowByTop.set(topLayer, row);
    }
    row.nodeIds.push(node.id);
    row.height = Math.max(row.height, layerSize);
  }
  const rows = Array.from(rowByTop.values()).sort((a, b) => a.topLayer - b.topLayer);
  if (rows.length < MIN_ROWS_FOR_COMPRESSION) return;

  // Compute current total gap budget vs target. If the target requires
  // shrinking gaps below the minimum, we still apply the floor — the
  // resulting ratio may be above the threshold but the layout is as
  // tight as possible without overlap.
  let originalTotalGap = 0;
  const originalGaps: number[] = [];
  for (let i = 0; i < rows.length - 1; i++) {
    const gap = rows[i + 1]!.topLayer - (rows[i]!.topLayer + rows[i]!.height);
    originalGaps.push(gap);
    originalTotalGap += Math.max(0, gap);
  }
  if (originalTotalGap <= 0) return;

  let totalRowHeight = 0;
  for (const row of rows) totalRowHeight += row.height;

  const desiredTotalGap = Math.max(0, targetLayerSpan - totalRowHeight);
  const gapScale = desiredTotalGap > 0 ? Math.min(1, desiredTotalGap / originalTotalGap) : 0;
  if (gapScale >= 1) return; // no compression needed

  // Apply uniform scale with a per-gap floor at MIN_COMPRESSED_LAYER_GAP_PX
  // (only positive gaps — zero/negative gaps are passed through as-is
  // since they're already overlap or coincident).
  const newGaps = originalGaps.map((g) =>
    g <= 0 ? g : Math.max(MIN_COMPRESSED_LAYER_GAP_PX, Math.round(g * gapScale)),
  );

  // Walk rows in order, shift each node to its row's new layer position.
  // The first row stays put; subsequent rows shift up by the accumulated
  // gap reduction.
  let layerCursor = rows[0]!.topLayer + rows[0]!.height;
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i]!;
    const oldTop = row.topLayer;
    const newTop = layerCursor + newGaps[i - 1]!;
    const delta = newTop - oldTop;
    if (Math.abs(delta) >= 1) {
      for (const id of row.nodeIds) {
        const pos = positions.get(id);
        if (!pos) continue;
        if (isHorizontal) pos.x = Math.round(pos.x + delta);
        else pos.y = Math.round(pos.y + delta);
      }
      row.topLayer = newTop;
    }
    layerCursor = row.topLayer + row.height;
  }
}

/**
 * Uses topological layering with even spacing.
 */
function fallbackLayout(
  input: LayoutInput,
  direction: LayoutDirection
): LayoutOutput {
  const isHorizontal = direction === 'RIGHT';
  const nodeIds = new Set(input.nodes.map((n) => n.id));

  // Compute layers via longest-path
  const incomingMap = new Map<string, string[]>();
  for (const n of input.nodes) incomingMap.set(n.id, []);
  for (const e of input.edges) {
    if (nodeIds.has(e.source) && nodeIds.has(e.target)) {
      incomingMap.get(e.target)?.push(e.source);
    }
  }

  const layers = new Map<string, number>();
  const visited = new Set<string>();

  function assignLayer(id: string): number {
    if (visited.has(id)) return layers.get(id) ?? 0;
    visited.add(id);
    const parents = incomingMap.get(id) ?? [];
    if (parents.length === 0) {
      layers.set(id, 0);
      return 0;
    }
    const maxParent = Math.max(...parents.map((p) => assignLayer(p)));
    const layer = maxParent + 1;
    layers.set(id, layer);
    return layer;
  }

  for (const n of input.nodes) assignLayer(n.id);

  // Group by layer
  const layerGroups = new Map<number, string[]>();
  for (const n of input.nodes) {
    const l = layers.get(n.id) ?? 0;
    if (!layerGroups.has(l)) layerGroups.set(l, []);
    layerGroups.get(l)!.push(n.id);
  }

  const positions: LayoutOutput = new Map();
  for (const [layer, group] of layerGroups) {
    const totalCross = (group.length - 1) * LAYOUT_CONFIG.nodeSpacing;
    const startCross = -totalCross / 2;
    group.forEach((id, i) => {
      const layerPos = layer * LAYOUT_CONFIG.layerSpacing + LAYOUT_CONFIG.originOffset;
      const crossPos = startCross + i * LAYOUT_CONFIG.nodeSpacing + LAYOUT_CONFIG.originOffset;
      positions.set(id, toPosition(layerPos, crossPos, isHorizontal));
    });
  }

  return positions;
}

/**
 * Convert React Flow nodes/edges to layout input format
 */
export const convertToLayoutInput = (
  nodes: Array<{
    id: string;
    width?: number;
    height?: number;
    measured?: { width?: number; height?: number } | undefined;
    /**
     * Action node data. We read `scopeParentId` and `branchType` so the
     * layout engine can reserve extra space around nodes that sit inside
     * a *visually rendered* scope container — currently ForEach/Until loop
     * bodies (`branchType === 'loop'`). Children of If/Switch branches do
     * have a `scopeParentId` upstream, but no scope container is rendered
     * around them, so we deliberately do NOT inflate their layout space.
     */
    data?:
      | {
          scopeParentId?: string | undefined;
          branchType?: string | undefined;
        }
      | undefined;
  }>,
  edges: Array<{ id: string; source: string; target: string; sourceHandle?: string | null }>
): LayoutInput => {
  const layoutNodes: LayoutNodeInput[] = nodes.map((node) => {
    const layoutNode: LayoutNodeInput = {
      id: node.id,
      // Width: use the larger of the measured value and the default. Icon-only
      // wrappers report ~56px, but labels can extend the visual extent up to
      // ~240px — Sugiyama must reserve at least DEFAULT_NODE_WIDTH so adjacent
      // chains don't visually overlap. Going below the default would
      // re-introduce #959.
      width: Math.max(
        node.measured?.width ?? node.width ?? DEFAULT_NODE_WIDTH,
        DEFAULT_NODE_WIDTH
      ),
      // Height: keep DEFAULT_NODE_HEIGHT spacing regardless of measured wrapper
      // height. Wrappers can grow to ~90px when ClampedLabel renders a 2-line
      // label, but the layout already includes DEFAULT_NODE_GAP between rows,
      // so reserving the default per node leaves enough vertical clearance
      // without inflating fitView's bounding box (which shifts the viewport
      // and can move nodes onto e2e tests' hard-coded click coordinates).
      height: node.height ?? DEFAULT_NODE_HEIGHT,
    };
    // Only propagate `scopeParentId` for nodes inside a loop body —
    // `branchType === 'loop'` is set in `useWorkflowGraph` exclusively for
    // ForEach/Until children, which are the only nodes wrapped by a visible
    // scope container. If/Switch branch children also carry `scopeParentId`
    // upstream but must not get layout inflation here, since no halo is
    // rendered around them.
    if (node.data?.scopeParentId && node.data?.branchType === 'loop') {
      layoutNode.scopeParentId = node.data.scopeParentId;
    }
    return layoutNode;
  });

  const nodeIds = new Set(layoutNodes.map((n) => n.id));
  const layoutEdges: LayoutEdgeInput[] = edges
    .filter(
      (edge) =>
        !edge.id.startsWith('terminal-') && // Exclude terminal edges
        // Structural guard: never emit an edge whose endpoints are absent from
        // the node set (e.g. a collapsed scope's hidden descendants that the
        // caller filtered out of `nodes`). Feeding the Sugiyama layout an edge
        // that references a missing node can break it, so drop those here
        // regardless of which caller assembled the input.
        nodeIds.has(edge.source) &&
        nodeIds.has(edge.target)
    )
    .map((edge) => {
      const layoutEdge: LayoutEdgeInput = {
        id: edge.id,
        source: edge.source,
        target: edge.target,
      };
      // Only add sourceHandle if it exists (for exactOptionalPropertyTypes)
      if (edge.sourceHandle) {
        layoutEdge.sourceHandle = edge.sourceHandle;
      }
      return layoutEdge;
    });

  return { nodes: layoutNodes, edges: layoutEdges };
};

/**
 * Apply layout positions to React Flow nodes
 */
export const applyLayoutToNodes = <
  T extends { id: string; position: { x: number; y: number } },
>(
  nodes: T[],
  positions: LayoutOutput
): T[] => {
  let result: T[] | undefined;
  nodes.forEach((node, index) => {
    const newPosition = positions.get(node.id);
    if (
      !newPosition
      || (
        node.position.x === newPosition.x
        && node.position.y === newPosition.y
      )
    ) {
      return;
    }
    result ??= [...nodes];
    result[index] = { ...node, position: newPosition };
  });
  return result ?? nodes;
};

export const calculateScopeContainerBound = (
  node: ScopeContainerNodeInput,
  nodeById: ReadonlyMap<string, ScopeContainerNodeInput>,
): ScopeContainerBounds | undefined => {
  if (node.type !== 'scopeContainer') return undefined;
  const childIds = node.data?.childNodeIds;
  if (!childIds?.length) return undefined;

  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (const childId of childIds) {
    const child = nodeById.get(childId);
    if (!child) continue;
    const width = child.measured?.width ?? DEFAULT_NODE_WIDTH;
    const height = child.measured?.height ?? DEFAULT_NODE_HEIGHT;
    minX = Math.min(minX, child.position.x);
    minY = Math.min(minY, child.position.y);
    maxX = Math.max(maxX, child.position.x + width);
    maxY = Math.max(maxY, child.position.y + height);
  }

  if (!Number.isFinite(minX)) return undefined;
  const position = {
    x: minX - SCOPE_CONTAINER_PADDING,
    y: minY - SCOPE_CONTAINER_PADDING - SCOPE_CONTAINER_HEADER_OFFSET,
  };
  return {
    position,
    width: maxX + SCOPE_CONTAINER_PADDING - position.x,
    height: maxY + SCOPE_CONTAINER_PADDING - position.y,
  };
};

type RenderableScopeContainerNode = ScopeContainerNodeInput & {
  data?: (
    NonNullable<ScopeContainerNodeInput['data']>
    & {
      containerWidth?: number | undefined;
      containerHeight?: number | undefined;
    }
  ) | undefined;
};

export const applyScopeContainerBoundsToNodes = <
  T extends RenderableScopeContainerNode,
>(
  nodes: T[],
  scopeContainerIds?: ReadonlySet<string>,
): T[] => {
  const nodeById = new Map<string, T>();
  const nodeIndexById = new Map<string, number>();
  const discoveredScopeIds = new Set<string>();
  nodes.forEach((node, index) => {
    nodeById.set(node.id, node);
    nodeIndexById.set(node.id, index);
    if (!scopeContainerIds && node.type === 'scopeContainer') {
      discoveredScopeIds.add(node.id);
    }
  });
  const targetIds = scopeContainerIds ?? discoveredScopeIds;

  let result: T[] | undefined;

  for (const scopeId of targetIds) {
    const node = nodeById.get(scopeId);
    const index = nodeIndexById.get(scopeId);
    if (!node || index === undefined) continue;
    const bounds = calculateScopeContainerBound(node, nodeById);
    if (!bounds) continue;
    if (
      node.position.x === bounds.position.x
      && node.position.y === bounds.position.y
      && node.data?.containerWidth === bounds.width
      && node.data?.containerHeight === bounds.height
    ) {
      continue;
    }

    result ??= [...nodes];
    result[index] = {
      ...node,
      position: bounds.position,
      data: {
        ...node.data,
        containerWidth: bounds.width,
        containerHeight: bounds.height,
      },
    };
  }

  return result ?? nodes;
};

/**
 * Calculate positions and dimensions for scope container nodes
 * based on the bounding box of their child nodes.
 * Used after layout to size visual boundaries around ForEach/Until loop bodies.
 */
export const calculateScopeContainerBounds = (
  nodes: ReadonlyArray<ScopeContainerNodeInput>
): Map<string, ScopeContainerBounds> => {
  const bounds = new Map<string, ScopeContainerBounds>();

  // Build node lookup index O(n) once to avoid O(n²) child lookup
  const nodeById = new Map(nodes.map((n) => [n.id, n]));

  for (const node of nodes) {
    const calculated = calculateScopeContainerBound(node, nodeById);
    if (calculated) bounds.set(node.id, calculated);
  }

  return bounds;
};
